import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AddRestaurantModal from '../components/AddRestaurantModal';
import RestaurantOffersModal from '../components/RestaurantOffersModal';
import { getRestaurants } from '../services/restaurantService';

const PAGE_SIZE = 20;

const eyeIcon = (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const editIcon = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
  </svg>
);

const tagIcon = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
    <line x1="7" y1="7" x2="7.01" y2="7" />
  </svg>
);

const locationIcon = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
    <circle cx="12" cy="10" r="3" />
  </svg>
);

function RestaurantImage({ src, alt }) {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    setLoaded(false);
  }, [src]);

  return (
    <>
      {!loaded && <div className="restaurant-image-shimmer" aria-hidden="true" />}
      <img
        src={src}
        alt={alt}
        className={loaded ? 'is-loaded' : ''}
        onLoad={() => setLoaded(true)}
        onError={() => setLoaded(true)}
      />
    </>
  );
}

export default function Restaurants() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [modalRestaurant, setModalRestaurant] = useState(null);
  const [modalMode, setModalMode] = useState('add');
  const [offersRestaurant, setOffersRestaurant] = useState(null);
  const [reloadToken, setReloadToken] = useState(0);

  const sentinelRef = useRef(null);
  const prevFiltersRef = useRef({ statusFilter, debouncedSearch, sortBy });

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search.trim());
    }, 400);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    const prev = prevFiltersRef.current;
    const filtersChanged =
      prev.statusFilter !== statusFilter ||
      prev.debouncedSearch !== debouncedSearch ||
      prev.sortBy !== sortBy;

    prevFiltersRef.current = { statusFilter, debouncedSearch, sortBy };

    if (filtersChanged && page !== 1) {
      setPage(1);
      setRestaurants([]);
      setHasMore(true);
      return undefined;
    }

    let cancelled = false;
    const append = !filtersChanged && page > 1;

    async function loadRestaurants() {
      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
        if (filtersChanged) {
          setRestaurants([]);
          setHasMore(true);
        }
      }
      setError('');

      try {
        const response = await getRestaurants({
          page,
          limit: PAGE_SIZE,
          status: statusFilter || undefined,
          search: debouncedSearch || undefined,
          sort: sortBy,
        });

        if (cancelled) return;

        const data = Array.isArray(response.data) ? response.data : [];
        const totalPages = response.pagination?.totalPages || 1;

        setRestaurants((prevList) => (append ? [...prevList, ...data] : data));
        setHasMore(page < totalPages);
      } catch (err) {
        if (cancelled) return;
        setError(err.message || 'Failed to load partner venues.');
        if (!append) {
          setRestaurants([]);
          setHasMore(false);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
          setLoadingMore(false);
        }
      }
    }

    loadRestaurants();
    return () => {
      cancelled = true;
    };
  }, [page, statusFilter, debouncedSearch, sortBy, reloadToken]);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && hasMore && !loading && !loadingMore) {
          setPage((prev) => prev + 1);
        }
      },
      { root: null, rootMargin: '240px', threshold: 0 }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, loading, loadingMore, restaurants.length]);

  function handleCreated() {
    setPage(1);
    setRestaurants([]);
    setHasMore(true);
    setReloadToken((token) => token + 1);
  }

  function openAddModal() {
    setModalRestaurant(null);
    setModalMode('add');
    setShowAddModal(true);
  }

  function openCouponsPage(restaurant) {
    const id = restaurant?.id;
    if (!id) return;
    navigate(`/restaurants/${id}/coupons`, {
      state: { restaurant },
    });
  }

  function openEditModal(restaurant) {
    setModalRestaurant(restaurant);
    setModalMode('edit');
    setShowAddModal(true);
  }

  function closeModal() {
    setShowAddModal(false);
    setModalRestaurant(null);
    setModalMode('add');
  }

  function openOffersModal(restaurant) {
    setOffersRestaurant(restaurant);
  }

  function closeOffersModal() {
    setOffersRestaurant(null);
  }

  const showInitialLoading = loading && restaurants.length === 0;

  return (
    <div className="restaurants-page main-bg-top">
      <div className="restaurants-toolbar">
        <div className="users-search restaurants-search">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            type="text"
            placeholder="Search by names, address...."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="restaurants-toolbar-actions">
          <div className="filter-select-wrap">
            <select
              className="filter-select"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>

          <div className="filter-select-wrap">
            <select
              className="filter-select restaurants-sort-select"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
            >
              <option value="newest">Newest First</option>
              <option value="oldest">Oldest First</option>
            </select>
          </div>

          <button type="button" className="restaurants-add-btn" onClick={openAddModal}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            Add Partner Venue
          </button>
        </div>
      </div>

      {error && restaurants.length === 0 && <div className="restaurants-error">{error}</div>}

      {showInitialLoading ? (
        <div className="restaurants-empty">Loading partner venues...</div>
      ) : (
        <>
          <div className="restaurants-grid">
            {restaurants.map((restaurant) => {
              const statusLabel = restaurant.status === 'inactive' ? 'Inactive' : 'Active';
              const locationText = restaurant.location || restaurant.address || 'No location';

              return (
                <article key={restaurant.id} className="restaurant-card">
                  <div className="restaurant-card-image">
                    {restaurant.imageUrl ? (
                      <RestaurantImage src={restaurant.imageUrl} alt={restaurant.name} />
                    ) : (
                      <div className="restaurant-card-image-placeholder">No image</div>
                    )}
                  </div>

                  <div className="restaurant-card-body">
                    <div className="restaurant-card-title-row">
                      <h3>{restaurant.name}</h3>
                      <span className={`status-badge status-${statusLabel.toLowerCase()}`}>
                        {statusLabel}
                      </span>
                    </div>

                    <div className="restaurant-card-location">
                      {locationIcon}
                      <span>{locationText}</span>
                    </div>

                    <p className="restaurant-card-desc">
                      {restaurant.description || 'No description provided.'}
                    </p>

                    <div className="restaurant-card-actions">
                      <button type="button" className="restaurant-action-btn" onClick={() => openCouponsPage(restaurant)}>
                        {eyeIcon}
                        View
                      </button>
                      <button type="button" className="restaurant-action-btn" onClick={() => openEditModal(restaurant)}>
                        {editIcon}
                        Edit
                      </button>
                      <button
                        type="button"
                        className="restaurant-action-btn restaurant-action-btn-primary"
                        onClick={() => openOffersModal(restaurant)}
                      >
                        {tagIcon}
                        Offers
                      </button>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>

          {!loading && restaurants.length === 0 && (
            <div className="restaurants-empty">No partner venues found.</div>
          )}

          <div ref={sentinelRef} className="restaurants-scroll-sentinel" aria-hidden="true" />

          {loadingMore && (
            <div className="restaurants-loading-more">Loading more partner venues...</div>
          )}
        </>
      )}

      <AddRestaurantModal
        open={showAddModal}
        restaurant={modalRestaurant}
        mode={modalMode}
        onClose={closeModal}
        onCreated={handleCreated}
      />

      <RestaurantOffersModal
        open={Boolean(offersRestaurant)}
        restaurant={offersRestaurant}
        onClose={closeOffersModal}
      />
    </div>
  );
}
